import { Link } from "react-router-dom"
import { Activity, Building2, Users, HardDrive, FileText, BookOpen } from "lucide-react"
import { usePlan } from "@/hooks/usePlan"
import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import { BILLING_ENABLED } from "@/lib/appConfig"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

function formatStorage(gb: number, limitGb: number | null): { value: string; unit: string } {
    // If the limit is sub-1 GB (e.g. 0.2 = 200 MB), display in MB
    const useMB = limitGb !== null && limitGb < 1
    if (useMB) {
        return { value: Math.round(gb * 1024).toString(), unit: "MB" }
    }
    return { value: gb.toFixed(2).replace(/\.?0+$/, "") || "0", unit: "GB" }
}

function formatStorageLimit(limitGb: number | null): string {
    if (limitGb === null) return "∞"
    if (limitGb < 1) return `${Math.round(limitGb * 1024)} MB`
    return `${limitGb} GB`
}

export function UsageWidget() {
    const { data } = usePlan()

    if (!data) return null

    const usage = data.usage
    const planName = data.plan.name
    const storageLimit = usage.storage_gb.limit
    const storageUsed = formatStorage(usage.storage_gb.used, storageLimit)

    const metrics = [
        {
            label: "Properties",
            icon: Building2,
            used: usage.properties.used,
            limit: usage.properties.limit,
            pct: usage.properties.pct ?? 0,
            display: `${usage.properties.used} / ${usage.properties.limit ?? "∞"}`,
            href: "/properties",
        },
        {
            label: "Tenants",
            icon: Users,
            used: usage.tenants.used,
            limit: usage.tenants.limit,
            pct: usage.tenants.pct ?? 0,
            display: `${usage.tenants.used} / ${usage.tenants.limit ?? "∞"}`,
            href: "/tenants",
        },
        {
            label: "Leases",
            icon: BookOpen,
            used: usage.leases.used,
            limit: usage.leases.limit,
            pct: usage.leases.pct ?? 0,
            display: `${usage.leases.used} / ${usage.leases.limit ?? "∞"}`,
            href: "/leases",
        },
        {
            label: "Documents",
            icon: FileText,
            used: usage.documents.used,
            limit: usage.documents.limit,
            pct: usage.documents.pct ?? 0,
            display: `${usage.documents.used} / ${usage.documents.limit ?? "∞"}`,
            href: "/documents",
        },
        {
            label: "Storage",
            icon: HardDrive,
            used: usage.storage_gb.used,
            limit: storageLimit,
            pct: usage.storage_gb.pct ?? 0,
            display: `${storageUsed.value} ${storageUsed.unit} / ${formatStorageLimit(storageLimit)}`,
            href: "/documents",
        },
    ]

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative group">
                    <Activity className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72 p-2">
                <DropdownMenuLabel className="flex justify-between items-center px-2 py-1">
                    <span className="font-semibold text-sm">Usage Stats</span>
                    <span className="text-xs font-medium uppercase text-muted-foreground bg-secondary px-2 py-0.5 rounded-full">
                        {planName} plan
                    </span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="my-2" />

                <div className="space-y-3.5 px-2 py-1">
                    {metrics.map((m) => (
                        <Link key={m.label} to={m.href} className="block group/item">
                            <div className="space-y-1.5">
                                <div className="flex justify-between items-center text-sm">
                                    <div className="flex items-center gap-2 text-muted-foreground group-hover/item:text-foreground transition-colors">
                                        <m.icon className="h-3.5 w-3.5" />
                                        <span className="text-xs font-medium">{m.label}</span>
                                    </div>
                                    <span className="text-xs font-semibold text-foreground tabular-nums">
                                        {m.display}
                                    </span>
                                </div>
                                <Progress
                                    value={m.pct}
                                    className={`h-1 ${m.pct >= 90 ? "[&>div]:bg-destructive" : m.pct >= 70 ? "[&>div]:bg-amber-500" : ""}`}
                                />
                            </div>
                        </Link>
                    ))}
                </div>

                <DropdownMenuSeparator className="my-2" />
                {BILLING_ENABLED && (
                    <div className="p-1">
                        <Link to="/pricing">
                            <Button className="w-full text-sm" size="sm">
                                Upgrade Plan
                            </Button>
                        </Link>
                    </div>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    )
}
